import { Config } from "../config";
import { AuthResponse, Token } from "../models";
import { authenticateAsyncDebounced, selectAuthenticated, selectAuthResult, selectToken } from "../redux/authSlice";
import { getQueryParam, toQueryString } from "../utils/uri";
import { useAppDispatch, useAppSelector } from "./hooks";

const LOCAL_STORAGE_SESSION_ID = "session_id";

export function buildGoogleAuthFlowUrl() {
  const state = window.location.pathname + window.location.search; // set state to restore current position in the app on login complete
  const params = {
    scope: Config.googleAuth.scopes.join(" "),
    include_granted_scopes: "true",
    response_type: "code",
    client_id: Config.googleAuth.clientId,
    redirect_uri: Config.googleAuth.redirectUri,
    state: state,
    access_type: "offline",
  };
  return `${Config.googleAuth.oauthUrl}?${toQueryString(params)}`;
}

export async function authenticate(params: { authCode?: string; sessionId?: string | null }): Promise<AuthResponse | null> {
  try {
    const method = params.authCode ? "login" : "refresh";
    const response = await fetch(`${Config.api.uri}/Auth/${method}?${toQueryString(params)}`, {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    });

    return (await response.json()) as AuthResponse;
  } catch (error) {
    console.error("Fetch failed:", error);
    throw error;
  }
}

export function getCachedSessionId(): string | null {
  return localStorage.getItem(LOCAL_STORAGE_SESSION_ID);
}

export function cacheSessionId(sessionId?: string | null) {
  if (!sessionId) {
    localStorage.removeItem(LOCAL_STORAGE_SESSION_ID);
    return;
  }
  localStorage.setItem(LOCAL_STORAGE_SESSION_ID, sessionId);
}

export async function login(): Promise<AuthResponse | null> {
  const sessionId = getCachedSessionId();
  if (sessionId) {
    return await authenticate({ sessionId });
  }

  const authCode = getQueryParam("code");
  const state = getQueryParam("state");
  if (authCode) {
    // submit auth code to API to receive a session id in return
    const response = await authenticate({ authCode });
    if (!response?.isSuccess) {
      return response;
    }

    cacheSessionId(response.sessionId);
    const url = window.location.origin + window.location.pathname + window.location.hash;
    window.history.replaceState({}, document.title, url);

    if (state && state.length > 0) {
      console.log("restoring original url before login");
      window.location.href = window.location.origin + state;
    }

    return response;
  } else {
    // initiate auth code flow
    window.location.href = buildGoogleAuthFlowUrl();
    return null;
  }
}

export function isTokenValid(token?: Token) {
  if (!token) {
    return false;
  }
  // check token expiration
  const expirationTicks = 1000 * (token.exp ?? 0);
  const currentTimeTicks = Date.now();
  if (expirationTicks <= currentTimeTicks) {
    return false;
  }

  return true;
}

export function useAccessToken(): Token | null {
  const dispatch = useAppDispatch();
  const authenticated = useAppSelector(selectAuthenticated);
  if (!authenticated) {
    authenticateAsyncDebounced(dispatch);
  }
  return useAppSelector(selectToken);
}

export function useAuthResult(): AuthResponse | null {
  return useAppSelector(selectAuthResult);
}

export const logout = () => {
  cacheSessionId(null);
  window.location.reload();
};

export default {
  buildGoogleAuthFlowUrl,
  logout,
  useAccessToken,
  isTokenValid,
};
